// src/components/change-log-banner.tsx
"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, X } from "lucide-react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type ChangeLogBannerProps = {
  id: string;
  title: string;
  subtitle?: string;
  expiresAt: string | Date;
  startsAt?: string | Date;
  dialogTitle: string;
  markdown: string;
  storageKeyPrefix?: string;
};

function toDate(d: string | Date | undefined): Date | undefined {
  if (!d) return undefined;
  const dt = typeof d === "string" ? new Date(d) : d;
  return Number.isNaN(dt.getTime()) ? undefined : dt;
}

export default function ChangeLogBanner(props: ChangeLogBannerProps) {
  const {
    id,
    title,
    subtitle,
    expiresAt,
    startsAt,
    dialogTitle,
    markdown,
    storageKeyPrefix = "changelog_banner_dismissed_",
  } = props;

  const [mounted, setMounted] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const storageKey = `${storageKeyPrefix}${id}`;

  React.useEffect(() => {
    setMounted(true);

    const now = new Date();
    const exp = toDate(expiresAt);
    const start = toDate(startsAt);

    if (!exp) return;
    if (now > exp) return;
    if (start && now < start) return;

    try {
      const dismissed = localStorage.getItem(storageKey) === "1";
      if (dismissed) return;
    } catch {
      // ignore storage failures; still show
    }

    setVisible(true);
  }, [expiresAt, startsAt, storageKey]);

  const dismiss = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // ignore
    }
    setVisible(false);
    setOpen(false);
  };

  if (!mounted || !visible) return null;

  return (
    <>
      {/* Top-of-screen banner */}
      <div className="fixed inset-x-0 top-0 z-50">
        {/* Match card width */}
        <div className="mx-auto w-full max-w-[750px] px-4 pt-3">
          {/* IMPORTANT: not a <button>, so no nested button hydration error */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setOpen(true);
            }}
            className={cn(
              "w-full rounded-xl border px-4 py-2 text-left cursor-pointer",
              // Opaque, slightly yellow
              "bg-yellow-100 border-yellow-300 text-foreground",
              // Helpful focus ring for keyboard users
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60",
            )}
            aria-label="Open changes dialog"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{title}</div>
                {subtitle ? <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div> : null}
              </div>

              <div className="flex items-center gap-2">
                <Info className="h-6 w-6 shrink-0 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog with markdown + images */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl">{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="prose prose-sm max-w-none dark:prose-invert">
            {/* Keep default HTML <img> rendering (no Next/Image) */}
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown.trim()}</ReactMarkdown>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Got it!
            </Button>
            <Button type="button" onClick={dismiss}>
              Don&apos;t show again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
